/**
 * "Einheiten im Überblick" — rooms and current occupancy per unit (Slice 18).
 *
 * WHERE THE PER-UNIT NUMBERS COME FROM, and why not from the other candidate.
 *
 * The rows are the `units[]` of `GET /api/admin/occupancy` — the same response
 * `aggregateOccupancyForFilter` has aggregated since Slice 7, read one level
 * further down instead of summed. That payload is already loaded, so the table
 * costs no request, and it is the canonical CURRENT-STATE source: the server
 * applies `units_in_portfolio(units, day)` before building the list and
 * resolves lifecycle state against the day (`effective_tenancy_status`).
 *
 * It is deliberately NOT `co-living-metrics`' `occupied_count` / `free_count`.
 * Those are month-scoped (tenancy overlap with the billing month), have no
 * lease gate, and are exactly the semantics Slice 7 replaced. Reaching back for
 * them because a table needs per-unit numbers would reinstate the bug that
 * slice removed — a unit whose lease had ended still reporting rooms, and a
 * room vacated mid-month still reading as occupied for the whole month.
 *
 * A UNIT THAT IS NOT IN OPERATION IS ABSENT, NOT AT 0 %. Because the gate runs
 * server-side, a unit whose lease window has not opened simply has no row, and
 * therefore cannot appear here as a badly performing one. The caption names how
 * many of the inventory are in operation so the omission is visible rather than
 * silent. Both numbers are the ones the KPI row already shows.
 *
 * Unit labels are canonical display ids via `getUnitHeadingLabel` (Slice 15) —
 * never the raw id, which in production is a UUID.
 */
import React from "react";

import { getUnitHeadingLabel } from "../../../utils/unitDisplayId";
import { occupancyPercentText } from "../../../utils/occupancyPercent";

/**
 * Join the occupancy rows to the filtered unit population.
 *
 * The key is the INTERNAL unit id on both sides: `String(u.id ?? u.unitId)` is
 * what this page already matches `/api/admin/profit` and `/api/admin/occupancy`
 * rows with, and a display id is a label that two units may share.
 *
 * Exported for tests — the join is the logic of this file.
 *
 * @param {object|null} occupancyApi  raw `GET /api/admin/occupancy` response
 * @param {Array}       filteredUnits the page's filtered Co-Living population
 * @returns {Array<{id,label,totalRooms,occupiedRooms,freeRooms,occupiedRate}>}
 */
export function buildUnitOverviewRows(occupancyApi, filteredUnits) {
  if (!occupancyApi?.units || !Array.isArray(occupancyApi.units)) return [];
  const byId = new Map(
    (filteredUnits || []).map((u) => [String(u.id ?? u.unitId), u])
  );

  const rows = [];
  for (const row of occupancyApi.units) {
    const unit = byId.get(String(row.unit_id));
    if (!unit) continue; // outside the current filter, or not a Co-Living unit
    const totalRooms = Number(row.total_rooms ?? 0);
    const occupiedRooms = Number(row.occupied_rooms ?? 0);
    const freeRooms = Number(row.free_rooms ?? 0);
    rows.push({
      id: String(row.unit_id),
      label: getUnitHeadingLabel(unit),
      totalRooms,
      occupiedRooms,
      freeRooms,
      // Rounded here and nowhere else; the server publishes counts, not a
      // per-unit rate, and one decimal matches the aggregate rates beside it.
      occupiedRate:
        totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 1000) / 10 : 0,
    });
  }
  return rows;
}

/** Label → value → hint as adjacent siblings; see CoLivingKpiCard's header. */
function MiniStat({ label, value, hint }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="m-0 truncate text-[10px] font-medium text-muted-foreground">{label}</p>
      <p className="m-0 text-[15px] font-semibold leading-tight text-foreground">{value}</p>
      <p className="m-0 truncate text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}

const DASH = "—";

export default function CoLivingUnitsOverview({
  rows,
  operating,
  inventoryUnitCount,
  unavailableNote,
}) {
  return (
    <section
      aria-label="Einheiten im Überblick"
      className="flex flex-col rounded-[10px] border border-border bg-card p-4"
    >
      <h2 className="m-0 text-[13px] font-semibold text-foreground">Einheiten im Überblick</h2>
      <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
        Zimmer und aktuelle Belegung pro Einheit
      </p>

      {!operating ? (
        <p className="m-0 mt-4 text-[12px] leading-[1.5] text-muted-foreground">
          {unavailableNote}
        </p>
      ) : rows.length === 0 ? (
        <p className="m-0 mt-4 text-[12px] text-muted-foreground">
          Keine Einheiten im Betrieb.
        </p>
      ) : (
        /* Only the table scrolls sideways on a narrow viewport — the page
           itself never does. */
        <div className="mt-3 -mx-1 overflow-x-auto px-1">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">
              Zimmer, belegte und freie Zimmer sowie Auslastung je Einheit im Betrieb
            </caption>
            <thead>
              <tr>
                {["Einheit", "Zimmer", "Belegt", "Frei", "Auslastung"].map((h, i) => (
                  <th
                    key={h}
                    scope="col"
                    className={`border-b border-border pb-1.5 text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground ${
                      i === 0 ? "text-left" : "text-right"
                    } ${i === 4 ? "pl-3" : ""}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-b-0">
                  <th
                    scope="row"
                    className="py-2 text-left text-[12px] font-medium text-foreground"
                  >
                    {r.label}
                  </th>
                  <td className="py-2 text-right font-mono text-[12px] text-muted-foreground">
                    {r.totalRooms}
                  </td>
                  <td className="py-2 text-right font-mono text-[12px] text-foreground">
                    {r.occupiedRooms}
                  </td>
                  <td className="py-2 text-right font-mono text-[12px] text-muted-foreground">
                    {r.freeRooms}
                  </td>
                  <td className="py-2 pl-3 text-right">
                    <span className="flex items-center justify-end gap-2">
                      <span className="hidden h-[6px] w-[52px] overflow-hidden rounded-full bg-muted sm:block">
                        <span
                          className="block h-full rounded-full bg-emerald-500"
                          style={{ width: `${Math.min(100, Math.max(0, r.occupiedRate))}%` }}
                        />
                      </span>
                      <span className="font-mono text-[12px] font-medium text-foreground">
                        {/* OC3: a unit with no rooms has no rate. The row's
                            own counts are printed beside this unchanged. */}
                        {occupancyPercentText({
                          total: r.totalRooms,
                          rate: r.occupiedRate,
                        })}
                      </span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* The two aggregates that used to be their own tiles. They summarise the
          table above them, so they live with it — and they are rendered in
          EVERY state, dashing when the operating truth is unavailable. A card
          that simply disappears when a request fails tells the reader nothing;
          a dash tells them the question could not be answered. */}
      <div className="mt-3 flex gap-3 border-t border-border pt-3">
        <MiniStat
          label="Vollbelegte Units"
          value={operating ? operating.fullUnits : DASH}
          hint="Alle Rooms belegt"
        />
        <MiniStat
          label="Teilbelegte Units"
          value={operating ? operating.partialUnits : DASH}
          hint="Mindestens 1 Room belegt"
        />
      </div>

      {operating ? (
        <p className="m-0 mt-2 text-[11px] text-muted-foreground">
          {inventoryUnitCount === null || inventoryUnitCount === undefined
            ? DASH
            : `${operating.unitsCount} von ${inventoryUnitCount} Einheiten im Bestand sind im Betrieb`}
        </p>
      ) : null}
    </section>
  );
}
